import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { EventsFilterParams, EventType, EventsListResponse, CreateEventParams, UpdateEventParams } from '@/types/event'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import { fetchEvents, fetchEvent, createEvent, updateEvent, deleteEvent, bulkDeleteEvents } from '@/lib/api'

type EventStateType = {
  data: EventType | null
  isLoading: boolean
  error: string | null
}

type EventsListStateType = {
  data: EventsListResponse | null
  isLoading: boolean
  error: string | null
}

type EventsStoreState = {
  event: EventStateType
  eventsList: EventsListStateType
  filters: EventsFilterParams
  selectedEventIds: string[]
  setFilters: (filters: Partial<EventsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchEvents: (params?: EventsFilterParams) => Promise<EventsListResponse>
  resetEventsList: () => void

  // Event operations
  getEvent: (id: string) => Promise<EventType>
  createEvent: (params: CreateEventParams) => Promise<EventType>
  updateEvent: (id: string, params: UpdateEventParams) => Promise<EventType>
  deleteEvent: (id: string) => Promise<void>
  bulkDeleteEvents: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectEvent: (id: string) => void
  deselectEvent: (id: string) => void
  toggleEventSelection: (id: string) => void
  selectAllEvents: (events: EventType[]) => void
  deselectAllEvents: () => void
  isEventSelected: (id: string) => boolean
  resetEvent: () => void
}

const defaultFilters: EventsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined,
  beginDateStart: undefined,
  beginDateEnd: undefined,
  endDateStart: undefined,
  endDateEnd: undefined
}

const useEventsStore = create<EventsStoreState>()(
  devtools((set, get) => ({
    event: {
      data: null,
      isLoading: false,
      error: null
    },
    eventsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedEventIds: [],

    filters: defaultFilters,

    setFilters: filters => {
      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    resetFilters: () => {
      set({ filters: defaultFilters })
    },

    updateUrlParams: searchParams => {
      const filters: Partial<EventsFilterParams> = {}

      for (const [key, value] of searchParams.entries()) {
        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as EventsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof EventsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getEvent: async id => {
      set(state => ({
        event: {
          ...state.event,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchEvent(id)

        set(state => ({
          event: {
            ...state.event,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          event: {
            ...state.event,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createEvent: async params => {
      set(state => ({
        event: {
          ...state.event,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createEvent(params)

        set(state => ({
          event: {
            ...state.event,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          event: {
            ...state.event,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateEvent: async (id, params) => {
      set(state => ({
        event: {
          ...state.event,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateEvent(id, params)

        set(state => ({
          event: {
            ...state.event,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          event: {
            ...state.event,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteEvent: async id => {
      set(state => ({
        event: {
          ...state.event,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteEvent(id)

        set(state => ({
          event: {
            ...state.event,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          event: {
            ...state.event,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetEvent: () => {
      set(state => ({
        event: {
          ...state.event,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectEvent: id => {
      set(state => {
        if (state.selectedEventIds.includes(id)) {
          return state
        }
        return { selectedEventIds: [...state.selectedEventIds, id] }
      })
    },

    deselectEvent: id => {
      set(state => ({
        selectedEventIds: state.selectedEventIds.filter(eventId => eventId !== id)
      }))
    },

    toggleEventSelection: id => {
      set(state => {
        const isSelected = state.selectedEventIds.includes(id)
        if (isSelected) {
          return { selectedEventIds: state.selectedEventIds.filter(eventId => eventId !== id) }
        } else {
          return { selectedEventIds: [...state.selectedEventIds, id] }
        }
      })
    },

    selectAllEvents: events => {
      set({ selectedEventIds: events.map(event => event.id) })
    },

    deselectAllEvents: () => {
      set({ selectedEventIds: [] })
    },

    isEventSelected: id => {
      return get().selectedEventIds.includes(id)
    },

    bulkDeleteEvents: async ids => {
      set(state => ({
        event: {
          ...state.event,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteEvents({ ids })

        set(state => ({
          event: {
            ...state.event,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedEventIds: state.selectedEventIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          event: {
            ...state.event,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchEvents: async (params?: EventsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        eventsList: {
          ...state.eventsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchEvents(filtersToUse)

        set(state => ({
          eventsList: {
            ...state.eventsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          eventsList: {
            ...state.eventsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetEventsList: () => {
      set(state => ({
        eventsList: {
          ...state.eventsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useEventsStore
